import { PushMessage } from './push-message';
import { Sponsor } from './sponsor';
import { AuctionItem, ItemType } from './auction-item';
import { Dictionary, Utilities } from 'src/app/shared/helpers/util';
import { Category } from './category';
import { Guest } from './guest';
import { getCurrencySymbol } from '@angular/common';
import { Asset } from './asset';
import { ITimeParts, DateTimeHelper } from 'src/app/core/helper/datetime.helper';



export class Auction {

    access: AccessLevel;
    accessCode: string = this.getAccessCode();
    auctionId: number;
    bundlePath: string;
    newLogo: string;
    newBanner: string;
    logoPath: string;
    categories: string[] = [];
    newCategories: Category[] = [];
    pushMessages: PushMessage[] = [];
    description: string;
    name: string;
    qrCode: string;
    maxUsers: number;
    sponsorsEnabled: boolean = false;
    brandingEnabled: boolean = false;
    supportEnabled: boolean = false;
    removeArcBanner: boolean = false;
    enableExtendedAuction: boolean = false;
    maxImages: number;
    goal = 0;
    maxItems: number;
    extensionDuration = 0; // Default to 0 mins
    enableOnlinePayments = false;
    enableItemVideo = false;
    hasPaymentConfigured = false;
    currency = 'usd';
    endDateUtc: string = new Date().toLocaleString();
    startDateUtc: string = new Date().toLocaleString();
    requireActivatedEmail = false; // Default to open
    hideBidderName = false;
    hideItemsOnClose = false;
    hasTestPayment = false;
    extensionCount = 3; // Default to 3
    hasPreAuth = false;
    requireTicket = false; // for buying tickets through app_ticket
    enableTicketing = false;
    ticketPrice: number = 0;
    ticketCount: number = 0;
    watchedItems: string;
    raffleId: number;
    payInstructions: string; // max 1000
    hideRaised: boolean = false;
    maxAdmins: number = 2; // default to 2
    guests: Guest[];
    raffleCode: string;
    brandingColor: string;
    timezone: string;
    // for taxes
    tax1: number;
    tax1label: string; // max 50
    tax2: number;
    tax2label: string; // max 50
    fee1Label: string; // max 50
    fee2Label: string; // max 50

    // Type
    type: AuctionType

    // stripe connect configuration
    stripeAccountEmail: string;
    stripeAccountId: string;
    stripeSetupComplete = false;
    fee: number;

    // Organization
    orgNameForReceipt: string;
    orgUrl: string;
    orgDisplayUrl: string;
    orgDescription: string;
    orgPhone: string;
    orgEmail: string;
    orgContactName: string;
    orgFacebookUrl: string;
    orgInstagramHandle: string;
    orgTwitterHandle: string;
    orgLinkedInHandle: string;
    coverImagePath: string;
    orgProvince: string;
    orgCountry: string;
    orgNumber: string;
    orgCity: string;

    // CommunityId passed in from CommuniBee
    communityId: number;

    // We now store all payment settings on the auction
    // stripePublishKey: string;
    stripeSecretKey: string;
    stripeWebhookSecret: string;
    stripeVersion: string; // set by API, v1/v2

    // support mobile applications that haven't updated
    paypalClientId: string;
    payPalSecretKey: string;
    squareAccessToken: string;

    squareLocationId: string;
    squareVersion: string; // set by API, v1/v2
    squareWebhookSecret: string;

    braintreePublicKey = 'TemporaryPublicKey';
    braintreeSecretKey = 'TemporarySecret';
    braintreeMerchantId = 'TemporaryMerchant';
    surcharge: number;
    requireCcToBid = false;   // set to false by API if not using stripe
    reqCcDb: boolean = false; // sent down with true db value for dash to work with
    requireImmedPay = false;
    disableMaxBid = true; // disable max bid by default as users may be confused by it
    entryFee: number;
    deleted: boolean;
    paymentProviders: string[] = []; // comma separated list of payment providers
    pickupInstructions: string;
    feeCap: number;
    totalFeeCollected: number;
    totalTaxCollected: number;
    auctionTypeLabel = 'Auction';
    auctionTypeLabelLower = 'auction';
    bankingCopyState: BankingCopyState;


    // auctions have page associated, we need this for fundraiser mapping
    pageId: number;

    private endFmt: string;
    private startFmt: string;
    private currencySym = '$';
    private currUpper: string;
    private remaining: string;
    private remainingObj: ITimeParts;
    private productsDic: Dictionary<AuctionItem> = {};
    private sponsorsDic: Dictionary<Sponsor> = {};


    private didOpen = false;
    private didClose = false;

    constructor(auction?: Partial<Auction>) {
        Object.assign(this, auction);

        // add default sponsors if we need to
        if (this.removeArcBanner === false) {
            this.addDefaultSponsors();
        }

        this.currencySym = getCurrencySymbol(this.currency.toUpperCase(), 'narrow');

        // Set the push messages, then sort them
        this.pushMessages = this.pushMessages.map(m => new PushMessage(m));
        this.pushMessages = this.pushMessages.sort((a, b) => {
            return b.pushMessageId - a.pushMessageId;
        });

        // loop through the items ensuring dates are set
        for (const id of Object.keys(this.products)) {
            const prod = this.products[id];
            // any without an activate date get set to the auction start date
            if (!prod.openAt) {
                prod.openAt = this.startDateUtc;
            }
            // any without a deactivate date get set to the auction end date
            if (!prod.closeAt) {
                prod.closeAt = this.endDateUtc;
            }
        }

        // complete the initialization
        this.sortCategories();
        this.updateDates();
        this.update();

        // clean up the org url if we have one and set it on the display url using the utilities
        if (this.orgUrl) {
            this.orgDisplayUrl = Utilities.cleanUrl(this.orgUrl);
        }

        // set the auction type label
        if (this.isMarketplace) {
            this.auctionTypeLabel = 'Marketplace';
            this.auctionTypeLabelLower = 'marketplace';
        } else {
            this.auctionTypeLabel = 'Auction';
            this.auctionTypeLabelLower = 'auction';
        }

        // ensure a default value for the pickup instructions
        this.pickupInstructions = String.isNullOrWhiteSpace(this.pickupInstructions) ?
            `Your ${this.auctionTypeLabel} Administrator will manage distribution of winnings. You can use the in app chat to ask the ${this.auctionTypeLabelLower} admin for more details on how to claim your prize.` :
            this.pickupInstructions;

    }

    // Getters for various auction info
    get currencySymbol() { return this.currencySym; }
    get canEdit() { return (this.access > 10); }
    get isCreator() { return (this.access === 30); }
    get hasStarted() { return this.didOpen; }
    get hasEnded() { return this.didClose; }
    get isLive() { return (this.hasStarted === true && this.hasEnded === false); }
    get itemCount() { return Object.keys(this.products).length; }
    get startDisplay() { return this.startFmt; }
    get endDisplay() { return this.endFmt; }
    get timeRemaining() { return this.remaining; }
    get timeRemainingObj() { return this.remainingObj; }
    get itemsRemaining() { return this.maxItems - this.itemCount; }
    get currencyUpper() {
        if (this.currUpper == null) {
            this.currUpper = this.currency.toUpperCase();
        }
        return this.currUpper;
    }

    // Style (Free, Paid, RiskFree)
    get isFull() { return this.type === AuctionType.Full || this.type == AuctionType.OldByo || this.type === AuctionType.MarketPlace; }
    get isMarketplace() { return this.type === AuctionType.MarketPlace; }
    get isTestAuction() { return this.type === AuctionType.Demo; }
    get isBYO() { return this.type === AuctionType.OldByo; }

    get products() { return this.productsDic; }
    set products(vals: Dictionary<AuctionItem>) {
        for (const id of Object.keys(vals)) {
            this.productsDic[id] = new AuctionItem(vals[id]);
        }
    }

    get hasSponsors() { return Object.keys(this.sponsorsDic).length > 0; }

    get testPaymentRequired() {
        // test payment is required if we have setup Stripe, Square, or PayPal and
        // have no yet run a test payment
        if (this.stripeSetupComplete === true) {
            return false;
        }
        return (this.hasTestPayment === false && this.enableOnlinePayments === true && this.paymentProviders.length > 0);
    }

    get noBankingSetup() {
        // no banking setup if we have no banking info and we have no test payment
        return (this.isFull && this.enableOnlinePayments === true && this.hasTestPayment === false && this.paymentProviders.length === 0);
    }

    get sponsors() { return this.sponsorsDic; }
    set sponsors(vals: Dictionary<Sponsor>) {
        for (const id of Object.keys(vals)) {
            this.sponsorsDic[id] = new Sponsor(vals[id]);
        }
    }

    update() {
        // determine if the auction has started or ended  
        const now = new Date().valueOf();
        this.didOpen = (new Date(this.startDateUtc).valueOf() <= now);
        const wasClosed = this.didClose;
        this.didClose = (new Date(this.endDateUtc).valueOf() <= now);

        // if the auction closed and wasn't closed before, then we need to make sure
        if (this.didClose === true && wasClosed === false) {
            console.log('Auction closed, closing all items.');
            // all of the items are closed
            for (const id of Object.keys(this.products)) {
                // if the item is active, then we need to close it
                if (this.products[id].hasClosed === false) {
                    this.products[id].closeAt = this.endDateUtc;
                    this.products[id].updateDates();
                    this.products[id].update();
                }
            }
        }

        // Update the time remaining for the auction
        if (this.hasEnded === true) {
            this.remaining = 'Auction has Ended';
        } else if (this.hasStarted === false) {
            const remaining = DateTimeHelper.getTimeRemaining(undefined, new Date(this.startDateUtc).valueOf());
            this.remainingObj = remaining.parts;
            this.remaining = `${remaining.clock}`;
        } else {
            const remaining = DateTimeHelper.getTimeRemaining(new Date(this.startDateUtc).valueOf(), new Date(this.endDateUtc).valueOf());
            this.remainingObj = remaining.parts;
            this.remaining = `${(this.extensionDuration > 0 ? '~' : '')}${remaining.clock}`;
        }

        // Loop the items and call update on them
        for (const id of Object.keys(this.products)) {
            this.products[id].update();
        }

    }

    updateDates() {
        // Then refresh the date display for the auction
        if (this.startDateUtc && !this.startDateUtc.endsWith('Z')) {
            this.startDateUtc = this.startDateUtc + 'Z';
        }
        if (this.endDateUtc && !this.endDateUtc.endsWith('Z')) {
            this.endDateUtc = this.endDateUtc + 'Z';
        }

        this.startFmt = DateTimeHelper.formatDate(this.startDateUtc);
        this.endFmt = DateTimeHelper.formatDate(this.endDateUtc);
    }

    // includes reserve not met, should be used for active auctions only
    getTotalBidAmount(includeCurrency: boolean = true) {
        const now = new Date().valueOf();
        const isEnded = (new Date(this.endDateUtc).valueOf() <= now);
        let total = 0;

        for (const id of Object.keys(this.products)) {
            var product = this.products[id];
            switch (product.type) {
                case ItemType.BuyItNow:
                case ItemType.Donation:
                    total += product.bids.reduce((sum, bid) => sum + bid.value, 0);
                    break;

                case ItemType.LiveItem:
                case ItemType.BidItem:
                    const highestBid = product.bids.reduce((max, bid) => bid.value > max ? bid.value : max, 0);

                    // if it's a bid/item then we need to exclude the bid IF it's below the reserve price
                    // if the auction has ended.
                    if (!isEnded || (isEnded && highestBid >= product.reserveAmount)) {
                        total += highestBid;
                    }
                    break;

                case ItemType.BlindBid:
                    if (isEnded) {
                        const highestBlindBid = product.bids.reduce((max, bid) => bid.value > max ? bid.value : max, 0);
                        if (highestBlindBid >= product.reserveAmount) {
                            total += highestBlindBid;
                        }
                    }
                    break;
            }
        }

        return (includeCurrency) ? `${this.currencySym || '$'}${total}` : total;
    }

    getGoalProgress() {
        if (this.goal > 0) {
            return (+this.getTotalBidAmount(false) / this.goal);
        } else {
            return 100;
        }
    }

    getTotalBids() {
        let bids = 0;
        for (const id of Object.keys(this.products)) {
            bids += this.products[id].bidCount;
        }
        return bids;
    }

    getImageCount() {
        let count = 0;
        for (const key of Object.keys(this.products)) {
            count += this.products[key].images.length;
        }
        return count;
    }

    getItemsValue() {
        let val = 0;
        for (const key of Object.keys(this.products)) {
            const prod = this.products[key];
            if (prod.type === ItemType.BuyItNow) {
                // if storefront then qty * price
                if (this.isMarketplace) {
                    val += prod.quantity * prod.startingBid;
                } else {
                    // if not storefront then qty + total bids (if any) * price
                    val += (prod.quantity + prod.bidCount) * prod.startingBid;
                }
            } else {
                // its whatever is bigger of the starting bid or the approximate value (if any)
                val += Math.max(prod.startingBid || 0, prod.approximateValue || 0);
            }
        }
        return val;
    }

    canLeaveRiskFree() {
        if (this.stripeSetupComplete === true) {
            // if we setup stripe, we can't leave
            return false;
        }
        // or if we have used any of the paid features we can't leave
        return !this.isUsingPaidFeatures();
    }

    canSwitchType() {
        if (this.isFull) {
            // we are risk free, so we can change if we have not used any of the paid features
            return this.canLeaveRiskFree();
        } else {
            // can switch type if we haven't used/bought any addons
            return !this.hasBoughtAddons();
        }
    }

    isUsingPaidFeatures() {

        const maxSponsors = (this.removeArcBanner === false) ? 3 : 0;

        // check if any of the items have a video link
        let hasVideo = false;
        for (const key of Object.keys(this.products)) {
            if (!String.isNullOrWhiteSpace(this.products[key].videoUrl)) {
                hasVideo = true;
                break;
            }
        }

        return (
            Object.keys(this.sponsors).length > maxSponsors ||
            this.logoPath != null ||
            this.coverImagePath != null ||
            this.itemCount > 5 ||
            this.getImageCount() > 5 ||
            this.brandingColor != null ||
            hasVideo === true
        );
    }

    hasEnabledFeatures() {
        // if the auction has any of the paid features enabled (could be RF)
        return (
            this.enableExtendedAuction ||
            this.enableItemVideo ||
            this.enableOnlinePayments ||
            this.enableTicketing ||
            this.brandingEnabled ||
            this.sponsorsEnabled ||
            this.removeArcBanner
        );
    }

    hasBoughtAddons() {
        // can't be risk free, and our auction can't be stock
        return (
            this.isFull == false &&
            (
                this.hasEnabledFeatures() == true ||
                this.maxImages > 10 ||
                this.maxItems > 10 ||
                this.maxAdmins > 1 ||
                this.maxUsers > 10
            )
        );
    }

    private getAccessCode() {
        let text = '';
        const possible = 'ABCDEFGHJKLMNPQRSTUVWXYZ234679';
        for (let i = 0; i < 6; i++) {
            text += possible.charAt(Math.floor(Math.random() * possible.length));
        }
        return text;
    }


    private sortCategories() {
        // Sort the categories
        this.newCategories.sort((a, b) => {
            if (a.sortOrder > b.sortOrder) {
                return 1;
            } else if (a.sortOrder < b.sortOrder) {
                return -1
            }
            return 0;
        });
    }

    private addDefaultSponsors() {
        // if the auction doesn't have disable arc banner addon, then
        // add a sponsor for launch57, bid beacon, and raffle rocket
        if (!this.removeArcBanner) {
            // first launch57
            this.sponsorsDic[0] = new Sponsor({
                sponsorId: 0,
                name: 'Launch57',
                url: 'https://www.launch57.com',
                sortOrder: 0,
                description: 'Saas solutions that improve lives through gamified learning and responsible gaming principles.',
                banner: new Asset({ path: './assets/image/l57-logo.png' })
            });

            // then bid beacon
            this.sponsorsDic[1] = new Sponsor({
                sponsorId: 1,
                name: 'Bid Beacon',
                url: 'https://www.bidbeacon.com',
                sortOrder: 1,
                description: 'Bid Beacon makes silent auctions stand out by bringing them online.  Make an impact, triple your revenue and leave a lasting impression on your guests.',
                banner: new Asset({ path: './assets/image/bb-logo.png' })
            });

            // then raffle rocket
            this.sponsorsDic[2] = new Sponsor({
                sponsorId: 2,
                name: 'Raffle Rocket',
                url: 'https://www.raffle-rocket.com',
                sortOrder: 2,
                description: 'Raffle Rocket makes it easy to run a variety of online fundraiser raffles and raise serious cash.  Raffle off cash, trips, other prizes or a combination of these in our easy to use dashboard.',
                banner: new Asset({ path: './assets/image/rr-logo.png' })
            });
        }
    }
}

export enum AuctionType {
    Demo = 0,
    Full = 1,
    OldByo = 2,
    MarketPlace = 3
}

export enum AccessLevel {
    Removed = 0,
    Waitlist = 5,
    Guest = 10,
    Admin = 20,
    Owner = 30
}

export enum BankingCopyState {
    NotCopied = 0,
    Pending = 1,
    Approved = 2,
    Rejected = 3
}